import {
  addYoutubeChannelApiV1,
  getGetCampaignApiV1QueryKey,
  getGetCampaignsApiV1QueryKey,
  refreshEstimatedVideoMetricsApiV1,
  useGetChannelsApiV1Suspense,
  useJoinCampaignYoutubeApiV1,
} from "@/api/__generated__/api.schema";
import { ChannelReviewStatus } from "@/api/__generated__/models";
import { KnownError } from "@/api/client";
import { Profile } from "@/components/profile";
import { routes } from "@/constants/url";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useChecklist } from "@/hooks/use-checklist";
import { useWorkspaceNavigate } from "@/lib/router/use-workspace-navigate";
import { WorkspaceLink } from "@/lib/router/workspace-link";
import { trackPixelReservedEvent } from "@/lib/track/pixel";
import { NewTrackingEvent, trackNewEvent } from "@/lib/track/track-event";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/utils/format";
import { invariant } from "@/utils/invariant";
import { stripNil } from "@/utils/strip-nil";
import {
  Alert,
  Button,
  Checkbox,
  Collection,
  DatePicker,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  List,
  ListItem,
  Loading,
  Separator,
  ShadowBox,
  Step,
  StepGroup,
  StepNext,
  StepPrevious,
  StepProgress,
  Typography,
  useModal,
  useStep,
} from "@backpac/monica";
import { useGoogleLogin } from "@react-oauth/google";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Temporal } from "temporal-polyfill";

const CAMPAIGN_UPLOAD_START_DATE = Temporal.PlainDate.from("2025-03-24");
const CAMPAIGN_UPLOAD_END_DATE = Temporal.PlainDate.from("2025-04-06");

interface Channel {
  id: string;
  name: string;
  customUrl: string;
  profileImageUrl: string;
}

interface ChannelMetrics {
  channelId: string;
  estimatedViews: {
    days: number;
    min: number;
    max: number;
    minUs: number;
    maxUs: number;
  }[];
  lastUpdatedAt: string;
}

function useFormCampaignId() {
  const { id } = useParams<{ id: string }>();
  invariant(id != null, "Campaign ID is required");

  return id;
}

export function CampaignFormPage() {
  const [channel, setChannel] = useState<Channel | null>(null);
  const [channelMetrics, setChannelMetrics] = useState<ChannelMetrics | null>(null);
  const [uploadDate, setUploadDate] = useState<Temporal.PlainDate | null>(null);

  return (
    <div className="h-full px-4 pb-16 desktop:px-0">
      <StepGroup>
        <StepProgress className="mb-8" />
        <Step value={1}>
          <RegisterChannel
            channel={channel}
            channelMetrics={channelMetrics}
            onSelectedChannelChange={setChannel}
            onChannelMetricsChange={setChannelMetrics}
          />
        </Step>
        <Step value={2}>
          <SelectUploadDate
            uploadDate={uploadDate}
            channelMetrics={channelMetrics}
            onUploadDateChange={setUploadDate}
          />
        </Step>
        <Step value={3}>
          <ReviewDetails channel={channel} uploadDate={uploadDate} />
        </Step>
        <Step value={4}>
          <Confirm />
        </Step>
      </StepGroup>
    </div>
  );
}

function RegisterChannel({
  channel,
  channelMetrics,
  onSelectedChannelChange,
  onChannelMetricsChange,
}: {
  channel: Channel | null;
  channelMetrics: ChannelMetrics | null;
  onSelectedChannelChange: (channel: Channel) => void;
  onChannelMetricsChange: (metrics: ChannelMetrics) => void;
}) {
  const {
    mutate: updateScopeChannel,
    error,
    loading,
  } = useFetchChannelEstimatedViews({
    selectedChannelId: channel?.id ?? "", // TODO: hacky workaroudn for the case that the channel is not selected
    onSuccess: onChannelMetricsChange,
  });
  const campaignId = useFormCampaignId();

  const displayValues = useMemo(() => {
    if (channelMetrics?.channelId !== channel?.id) {
      return { estimated7day: undefined, estimated7dayUs: undefined, lastUpdatedAt: undefined };
    }

    const estimated7day = channelMetrics?.estimatedViews.find((view) => view.days === 7);
    const estimated7dayUs = channelMetrics?.estimatedViews.find((view) => view.days === 7);
    const lastUpdatedAt = channelMetrics?.lastUpdatedAt;
    return { estimated7day, estimated7dayUs, lastUpdatedAt };
  }, [channelMetrics, channel]);

  useEffect(() => {
    trackNewEvent(NewTrackingEvent.ViewCampaignRegisterChannel, {
      campaign_id: campaignId,
    });
  }, [campaignId]);

  return (
    <section>
      <CampaignFormPage.Header
        title="Which channel will you use to upload your Shorts?"
        description="First, select your channel, then click the 'Check Estimated Views' button to preview your potential views."
      />
      <div className="flex flex-col gap-2 mb-8">
        <Typography size="sm" weight={600}>
          Channel
        </Typography>
        <RegisterChannel.ChannelSelect value={channel} onChange={onSelectedChannelChange} disabled={loading} />
        <ShadowBox className="p-4">
          <div className="flex items-center justify-between mb-4">
            <Typography size="sm" weight={600}>
              Estimated views
            </Typography>
            <Button
              variant="outlined"
              loading={loading}
              disabled={channel?.id == null}
              onClick={() => {
                trackNewEvent(NewTrackingEvent.ClickCheckEstimatedViews, {
                  campaign_id: campaignId,
                  channel_id: channel?.id ?? "",
                });
                updateScopeChannel();
              }}
            >
              Check estimated views
            </Button>
          </div>
          {error ? (
            <Alert theme="red" className="mb-4">
              {error}
            </Alert>
          ) : null}
          <Typography size="xs" color="textMuted" className="bg-backgroundDark rounded-[8px] p-4 space-y-[6px]">
            {loading ? (
              <div className="flex flex-col items-center justify-center gap-3">
                <Loading />
                <Typography size="2xs" color="textMuted">
                  Loading may take up to a minute, depending on the situation
                </Typography>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span>Channel name:</span>
                  <span>{channel?.name ?? "--"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Estimated min.-max. views(7 days):</span>
                  <span>
                    {displayValues.estimated7day?.min} - {displayValues.estimated7day?.max}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>
                    <b>Estimated min.-max. views(7 days, US):</b>
                  </span>
                  <span>
                    <b>
                      {displayValues.estimated7dayUs?.minUs} - {displayValues.estimated7dayUs?.maxUs}
                    </b>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Estimated view data last updated(PDT):</span>
                  <span>{displayValues.lastUpdatedAt ?? "-"}</span>
                </div>
              </>
            )}
          </Typography>
        </ShadowBox>
      </div>
      <div className="flex justify-end">
        <StepNext disabled={channel == null || channel.id !== channelMetrics?.channelId}>Next</StepNext>
      </div>
    </section>
  );
}

RegisterChannel.ChannelSelect = function ChannelSelect({
  value,
  onChange,
  disabled,
}: {
  value: Channel | null;
  onChange: (channel: Channel) => void;
  disabled?: boolean;
}) {
  const workspaceId = useWorkspaceId();
  const campaignId = useFormCampaignId();
  const { data: channelsData } = useGetChannelsApiV1Suspense(workspaceId);
  const channels = channelsData?.data.items.filter((c) => c.reviewStatus === ChannelReviewStatus.APPROVED) ?? [];
  const [open, setOpen] = useState(false);

  return (
    <div className="mb-8">
      <Dropdown open={open} onOpenChange={setOpen}>
        <DropdownTrigger className="w-full" disabled={disabled}>
          <ShadowBox className="p-4">
            {value != null ? (
              <div className="flex items-center justify-between">
                <Profile name={value.name} description={value.customUrl} profileImageUrl={value.profileImageUrl} />
                <i className="i-bp-chevron-down size-4" />
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="size-12 rounded-full bg-borderLight" />
                  <Typography size="xs">Select channel</Typography>
                </div>
                <i className="i-bp-chevron-down size-4" />
              </div>
            )}
          </ShadowBox>
        </DropdownTrigger>
        <DropdownContent className="min-w-[320px] desktop:min-w-[480px]" align="start" sideOffset={4}>
          <Collection<Channel>
            data={channels}
            filter={{
              keys: ["name", "customUrl"],
            }}
          >
            <div className="mb-2">
              <Collection.SearchInput placeholder="Search channel" />
            </div>
            <Collection.Result<Channel>>
              {({ collections }) => (
                <div className="max-h-[300px] overflow-y-auto">
                  {collections.length === 0 ? (
                    <div className="p-1 w-full text-center text-xs text-textSubtle">
                      {channels.length === 0 ? "No channels connected yet" : "No channels match your search"}
                    </div>
                  ) : (
                    collections.map((channel) => (
                      <DropdownItem
                        key={channel.id}
                        onSelect={(e) => {
                          e.preventDefault();
                          onChange(channel);
                          setOpen(false);
                          trackNewEvent(NewTrackingEvent.SelectCampaignChannel, {
                            channel_id: channel.id,
                            campaign_id: campaignId,
                          });
                        }}
                        className="flex items-center gap-2 px-2 py-2 w-full"
                      >
                        <Profile
                          name={channel.name}
                          description={channel.customUrl}
                          profileImageUrl={channel.profileImageUrl}
                        />
                      </DropdownItem>
                    ))
                  )}
                </div>
              )}
            </Collection.Result>
          </Collection>
        </DropdownContent>
      </Dropdown>
    </div>
  );
};

function useFetchChannelEstimatedViews({
  selectedChannelId,
  onSuccess,
}: {
  selectedChannelId: string;
  onSuccess: (metrics: ChannelMetrics) => void;
}) {
  const workspaceId = useWorkspaceId();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const updateScopeChannel = useGoogleLogin({
    ux_mode: "popup",
    flow: "auth-code",
    select_account: true,
    enable_serial_consent: false,
    scope:
      "email https://www.googleapis.com/auth/youtube.readonly https://www.googleapis.com/auth/yt-analytics.readonly",
    onNonOAuthError: () => {
      setLoading(false);
    },
    onSuccess: async (response) => {
      setLoading(true);
      setError(null);
      const { code } = response;
      let channel: Channel | undefined;
      try {
        try {
          const response = await addYoutubeChannelApiV1(workspaceId, {
            googleOAuthCode: code,
          });
          channel = stripNil(response.data).item.channel;
        } catch {
          setError("Channel Registration Failed. Please try again.");
          return;
        }

        if (channel.id !== selectedChannelId) {
          setError("Mismatched Channel Selection. Please use the selected channel to fetch estimated views.");
          return;
        }

        try {
          const { data: estimatedViewsData } = await refreshEstimatedVideoMetricsApiV1(workspaceId, channel.id);

          if (estimatedViewsData != null && estimatedViewsData.item != null) {
            onSuccess({
              channelId: channel.id,
              estimatedViews: estimatedViewsData.item.metrics.map((views) => ({
                days: views.days,
                min: views.estimatedMinShortsViewCount,
                max: views.estimatedMaxShortsViewCount,
                minUs: views.estimatedMinShortsViewCountUS,
                maxUs: views.estimatedMaxShortsViewCountUS,
              })),
              lastUpdatedAt: Temporal.Instant.from(estimatedViewsData.item.lastUpdatedAt)
                .toZonedDateTimeISO("America/Los_Angeles")
                .toLocaleString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  hour12: false,
                })
                .replace(/at/, ""),
            });
            setError(null);
          }
        } catch (error) {
          if (KnownError.is(error)) {
            if (error.status === 401) {
              // Unauthorized 여기로 안 떨어짐.
              setError("YouTube Analytics access not granted. Please allow access on the Google Sign in page.");
            } else if (error.status === 403) {
              // Forbidden
              setError("You must have at least 10 Shorts uploaded before the last 30 days to participate.");
            } else {
              setError("Channel Registration Failed. Please try again.");
            }
          }
          throw error;
        }
      } finally {
        setLoading(false);
      }
    },
  });

  function mutate() {
    updateScopeChannel();
  }

  return { mutate, error, loading };
}

function SelectUploadDate({
  uploadDate,
  channelMetrics,
  onUploadDateChange,
}: {
  uploadDate: Temporal.PlainDate | null;
  channelMetrics: ChannelMetrics | null;
  onUploadDateChange: (date: Temporal.PlainDate) => void;
}) {
  const campaignId = useFormCampaignId();
  invariant(channelMetrics != null, "Channel metrics is required");

  useEffect(() => {
    trackNewEvent(NewTrackingEvent.ViewCampaignRegisterUploadDate, {
      campaign_id: campaignId,
    });
  }, [campaignId]);

  return (
    <section>
      <CampaignFormPage.Header
        className="mb-8"
        title="When would you like to upload your Shorts?"
        description="Upload your Shorts on the selected date to maximize your views. Make sure to upload on your selected date to be eligible for monetization."
      />
      <ShadowBox className="px-4 py-6 mb-5">
        <DatePicker
          selectedDate={uploadDate ?? undefined}
          onDateSelected={(date) => {
            onUploadDateChange(date);
            trackNewEvent(NewTrackingEvent.SelectCampaignUploadDate, {
              campaign_id: campaignId,
              upload_date: date.toString(),
            });
          }}
          minDate={CAMPAIGN_UPLOAD_START_DATE}
          maxDate={CAMPAIGN_UPLOAD_END_DATE}
        />
        <Separator className="mt-1 mb-4" />
        <Typography size="sm" weight={600} className="text-center">
          Upload date:{" "}
          {uploadDate?.toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Typography>
      </ShadowBox>
      {uploadDate != null ? (
        <SelectUploadDate.Summary selectedDate={uploadDate} channelMetrics={channelMetrics} className="mb-8" />
      ) : null}
      <div className="flex justify-between">
        <StepPrevious>Back</StepPrevious>
        <StepNext disabled={uploadDate == null}>Next</StepNext>
      </div>
    </section>
  );
}

SelectUploadDate.Summary = function Summary({
  selectedDate,
  channelMetrics,
  className,
}: { selectedDate: Temporal.PlainDate; channelMetrics: ChannelMetrics; className?: string }) {
  const diffDays = selectedDate.until(CAMPAIGN_UPLOAD_END_DATE).days + 1;
  const estimatedViews = channelMetrics.estimatedViews.find((view) => view.days === diffDays);

  invariant(estimatedViews != null, "Estimated views is required");

  return (
    <section className={cn("px-4 py-4 bg-backgroundDark", className)}>
      <div className="flex gap-3">
        <i className="i-bp-youtube-bold inline-block size-4" />
        <div>
          <Typography weight={600} size="sm" className="mb-[6px]">
            Estimated views and reward
          </Typography>
          <Typography size="xs" color="textSecondary" className="mb-3">
            {selectedDate.toLocaleString("en-US", {
              month: "long",
              day: "numeric",
            })}{" "}
            -{" "}
            {CAMPAIGN_UPLOAD_END_DATE.toLocaleString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
            {diffDays === 1 ? " (1 day)" : ` (${diffDays} days)`}
          </Typography>
          <List className="mb-4">
            <Typography asChild size="paragraphXs" color="textMuted">
              <ListItem className="grid grid-cols-2 gap-2">
                <dt>Estimated views(US)</dt>
                <dd className="text-textPrimary">
                  {estimatedViews.minUs} - {estimatedViews.maxUs}
                </dd>
              </ListItem>
            </Typography>
            <Typography asChild size="paragraphXs" color="textMuted">
              <ListItem className="grid grid-cols-2 gap-2">
                <dt>Estimated reward</dt>
                <dd className="text-textPrimary">
                  {formatCurrency(estimatedViews.minUs / 1000, 2, { roundingMode: "floor" })} -{" "}
                  {formatCurrency(estimatedViews.maxUs / 1000, 2, { roundingMode: "floor" })}
                </dd>
              </ListItem>
            </Typography>
          </List>
          <Typography size="xs" color="textMuted" className="italic">
            Based on the target country: United States
          </Typography>
        </div>
      </div>
    </section>
  );
};

function ReviewDetails({ channel, uploadDate }: { channel: Channel | null; uploadDate: Temporal.PlainDate | null }) {
  const workspaceId = useWorkspaceId();
  const campaignId = useFormCampaignId();
  const queryClient = useQueryClient();
  const { showModal } = useRegistrationErrorMessage();
  const { next } = useStep();
  const { mutate: registerCampaign, isPending } = useJoinCampaignYoutubeApiV1({
    mutation: {
      onSuccess: async () => {
        trackNewEvent(NewTrackingEvent.JoinCampaign, {
          campaign_id: campaignId,
        });
        await Promise.all([
          queryClient.refetchQueries({ queryKey: getGetCampaignApiV1QueryKey(workspaceId, campaignId) }),
          queryClient.refetchQueries({ queryKey: getGetCampaignsApiV1QueryKey(workspaceId) }),
        ]);
        trackPixelReservedEvent("CompleteRegistration");
        next();
      },
      onError: (error) => {
        if (KnownError.is(error)) {
          showModal(error.status);
        }
      },
    },
  });
  const handleSubmit = useCallback(() => {
    invariant(channel != null, "Channel is required");
    invariant(uploadDate != null, "Upload date is required");

    registerCampaign({
      workspaceId,
      campaignId,
      data: {
        channelId: channel.id,
        uploadDate: uploadDate.toString(),
      },
    });
  }, [channel, registerCampaign, uploadDate, workspaceId, campaignId]);

  const { toggle, isAllChecked, isChecked } = useChecklist({
    initialItems: Object.keys(terms).map((title) => ({
      id: title,
      checked: false,
    })),
  });

  useEffect(() => {
    trackNewEvent(NewTrackingEvent.ViewCampaignRegisterReview, {
      campaign_id: campaignId,
    });
  }, [campaignId]);

  return (
    <section>
      <CampaignFormPage.Header
        title="Review & Confirm your campaign details"
        description="Before joining the campaign, please review the details below. Make sure everthing is correct, and confirm that you've reviewed the important notes."
      />
      <ShadowBox className="p-6 mb-10 space-y-4">
        <div className="p-4 space-y-2 border border-borderLight rounded-[8px]">
          <Typography size="xs" color="textMuted">
            Upload channel
          </Typography>
          <Profile
            name={channel?.name ?? "--"}
            description={channel?.customUrl ?? "--"}
            profileImageUrl={channel?.profileImageUrl ?? ""}
          />
        </div>
        <div className="p-4 space-y-2 border border-borderLight rounded-[8px]">
          <Typography size="xs" color="textMuted">
            Upload date
          </Typography>
          <Typography size="sm" weight={600}>
            {uploadDate?.toLocaleString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </Typography>
        </div>
        <div className="p-4 space-y-3 border border-borderLight rounded-[8px]">
          <Typography size="sm" weight={600}>
            Notice
          </Typography>
          <div className="space-y-3">
            {Object.entries(terms).map(([title, contents], index) => {
              return (
                <details className="group" key={index}>
                  <summary className="flex items-center justify-between gap-2 cursor-pointer ">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        className="shadow-none"
                        checked={isChecked(title)}
                        onCheckedChange={() => toggle(title)}
                      />
                      <Typography size="xs" weight={500}>
                        {title}
                      </Typography>
                    </div>
                    <i className="i-bp-chevron-down size-4 transition-transform group-open:rotate-180" />
                  </summary>
                  <List className="py-[10px] pl-8">
                    {contents.map((content, index) => {
                      return (
                        <Typography asChild key={index} size="paragraphXs" color="textMuted">
                          <ListItem>{content}</ListItem>
                        </Typography>
                      );
                    })}
                  </List>
                </details>
              );
            })}
          </div>
        </div>
      </ShadowBox>
      <div className="flex justify-between">
        <StepPrevious>Back</StepPrevious>
        <Button disabled={!isAllChecked || isPending} onClick={handleSubmit}>
          Submit
        </Button>
      </div>
    </section>
  );
}

function useRegistrationErrorMessage() {
  const campaignId = useFormCampaignId();
  const navigate = useWorkspaceNavigate();
  const { open } = useModal();
  const errorMap: Record<number, { title: string; message: string }> = {
    403: {
      title: "Oops! Campian is now full",
      message:
        "Looks like this campaign has already reached its goal, so joining is no longer available. Thanks for your interest! Stay tuned for more opportunities.",
    },
    409: {
      title: "Oops! Already joined",
      message: "You've already joined this campaign. Please check your campaign page for more details.",
    },
  };

  return {
    showModal(statusCode: number) {
      return open({
        title: "",
        className: "max-w-[360px] p-6",
        withNoHeader: true,
        hideCloseButton: true,
        children: (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2 text-center">
              <Typography size="2xl">🥲</Typography>
              <Typography font="unbounded" size="lg" color="textPrimary">
                {errorMap[statusCode].title}
              </Typography>
              <Typography size="paragraphXs" color="textMuted">
                {errorMap[statusCode].message}
              </Typography>
            </div>
            <Button
              className="w-full"
              size="lg"
              onClick={() => {
                navigate(routes.CAMPAIGNS._views(campaignId));
              }}
            >
              Go back to event
            </Button>
          </div>
        ),
      });
    },
  };
}

const terms: Record<string, string[]> = {
  "Participation Criteria": [
    "Only BackPac users who have successfully registered for the campaign may participate.",
    "Only one video per channel can participate in this campaign.",
    "When uploading your video, please ensure you mark it as [Paid Promotion]",
  ],
  "Period and View Eligibility": [
    "The campaign will automatically end once the campaign period finishes or when the total target of 3 million views is reached, whichever comes first.",
    "Views generated after the campaign ends are not eligible for rewards.",
    "Eligible views for campaign participation are calculated based on YouTube Analytics reports accessed directly from each authorized channel.",
    "Internal view counts may differ from the real-time view counts shown in YouTube Studio.",
  ],
  Reward: [
    "Rewards will be finalized between March 24 and March 31.",
    "Videos deleted before this period will not be eligible for rewards.",
    "Videos uploaded on a different date than the one selected during registration will not receive rewards.",
    "Videos that do not follow the provided production guidelines will not be eligible for rewards.",
    "Videos must use only the designated track listed on the campaign page to qualify for rewards.",
    "Videos containing additional audio tracks or overlapping music will not be rewarded.",
  ],
  "Adsense and Video Impact": [
    "Using the designated track will not impact AdSense earnings generated by your Shorts videos.",
  ],
};

export function Confirm() {
  const campaignId = useFormCampaignId();

  useEffect(() => {
    trackNewEvent(NewTrackingEvent.ViewCampaignRegisterComplete, {
      campaign_id: campaignId,
    });
  }, [campaignId]);

  return (
    <div className="h-full space-y-6">
      <CampaignFormPage.Header
        title="Thank you for joining the campaign!"
        description="You're all set! Now, focus on creating a great Shorts and make sure to upload it on your selected date. Follow the campaign guidelines, and let's make this a success!"
      />
      <WorkspaceLink to={routes.CAMPAIGNS._detail(campaignId)}>
        <Button className="flex mx-auto">View detail</Button>
      </WorkspaceLink>
    </div>
  );
}

CampaignFormPage.Header = function Header({
  title,
  description,
  className,
}: { title: string; description: string; className?: string }) {
  return (
    <div className={cn("space-y-3 mb-8", className)}>
      <Typography asChild font="unbounded" size="xl">
        <h1>{title}</h1>
      </Typography>
      <Typography size="paragraphXs" color="textMuted">
        {description}
      </Typography>
    </div>
  );
};
